import { useCallback, useEffect, useState } from 'react';
import { ChartSeriesConfig } from './chartSeries';

export interface ChartLegendVisibility {
  isSeriesHidden: (entryKey: string) => boolean;
  toggleSeries: (entryKey: string) => void;
}

/**
 * Legend keys for a chart whose legend entries are its data streams, which is
 * every chart except a pie drawing a single stream - that one legends its
 * individual slices and builds its own keys.
 */
export const getChartSeriesLegendKeys = (series: ChartSeriesConfig[]): string[] => {
  return series.map((item: ChartSeriesConfig): string => `${item.dataKey}:${item.name}`);
};

/**
 * Per-chart record of legend entries hidden by clicking them. Purely local UI
 * state: it resets when the widget unmounts and is never persisted to the
 * widget configuration.
 *
 * `entryKeys` are the keys the legend currently offers, in order. They are
 * positional in some charts (a single-series pie legends its slices as
 * `<dataKey>-<index>`), so a settings edit or a reshaped result that adds,
 * removes or reorders entries would re-point stale keys at different data -
 * and could even leave a chart's only remaining entry hidden with no clickable
 * legend entry to restore it. Reset whenever the keys change; plain data
 * refreshes keep the same keys and therefore the user's selection.
 */
export const useChartLegendVisibility = (entryKeys: string[]): ChartLegendVisibility => {
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  const entrySignature = entryKeys.join('|');

  useEffect(() => {
    setHiddenKeys((currentKeys: string[]): string[] => {
      return currentKeys.length === 0 ? currentKeys : [];
    });
  }, [entrySignature]);

  const toggleSeries = useCallback((entryKey: string): void => {
    setHiddenKeys((currentKeys: string[]): string[] => {
      return currentKeys.includes(entryKey)
        ? currentKeys.filter((key: string): boolean => key !== entryKey)
        : [...currentKeys, entryKey];
    });
  }, []);

  const isSeriesHidden = useCallback(
    (entryKey: string): boolean => {
      return hiddenKeys.includes(entryKey);
    },
    [hiddenKeys]
  );

  return { isSeriesHidden, toggleSeries };
};
